/**
 * Authentication Hooks
 * React Query hooks for authentication operations
 */

import type { ApiError } from "@shared-types/api.types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { toast } from "sonner"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useRefreshUserProfile } from "@/features/profile/hooks/useRefreshUserProfile"
import * as userService from "@/features/profile/services/user.service"
import * as authService from "../services/auth.service"

/**
 * Login Hook
 */
export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authService.login,
    onSuccess: data => {
      // Update user in cache
      queryClient.setQueryData(["user"], data.user)
      toast.success("Login successful")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Login failed"
      toast.error(message)
      // Throw error so calling component can handle it
      throw error
    },
  })
}

/**
 * Signup Hook
 */
export function useSignup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authService.signup,
    onSuccess: data => {
      // Update user in cache
      queryClient.setQueryData(["user"], data.user)
      toast.success("Account created successfully")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Signup failed"
      toast.error(message)
      // Throw error so calling component can handle it
      throw error
    },
  })
}

/**
 * Verify Email Hook
 */
export function useVerifyEmail() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore(state => state.setUser)

  return useMutation({
    mutationFn: authService.verifyEmail,
    onSuccess: data => {
      // Update user in Zustand store
      setUser(data.user)
      // Also update query cache for consistency
      queryClient.setQueryData(["user"], data.user)
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Verification failed"
      toast.error(message)
      // Throw error so calling component can handle it
      throw error
    },
  })
}

/**
 * Resend Verification Hook
 */
export function useResendVerification() {
  return useMutation({
    mutationFn: authService.resendVerification,
    onSuccess: () => {
      toast.success("Verification code sent")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to resend code"
      toast.error(message)
      // Throw error so calling component can handle it
      throw error
    },
  })
}

/**
 * Refresh Token Hook
 */
export function useRefreshToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ refreshToken, deviceId }: { refreshToken: string; deviceId: string }) =>
      authService.refreshToken(refreshToken, deviceId),
    onSuccess: data => {
      // Update user in cache
      queryClient.setQueryData(["user"], data.user)
    },
    onError: (error: unknown) => {
      // Token refresh failed, user will need to login again
      toast.error("Session expired. Please login again.")
      // Throw error so calling component can handle it
      throw error
    },
  })
}

/**
 * Get Profile Hook
 */
export function useProfile() {
  return useQuery({
    queryKey: ["user"],
    queryFn: userService.getProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })
}

/**
 * Update Profile Hook
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const { refreshUserProfile } = useRefreshUserProfile()
  const userId = useAuthStore(state => state.user?.id)

  return useMutation({
    mutationFn: userService.updateProfile,
    onSuccess: async () => {
      console.log("Profile update mutation success, fetching complete user data")

      // Fetch complete user data from backend and update both cache and auth store
      // This ensures we have the latest profile_photos, tags, and all relations
      try {
        await refreshUserProfile()
        console.log("User data refreshed successfully in auth store")
      } catch (error) {
        console.warn("Profile updated but failed to refresh user data:", error)
        // Profile update succeeded, so still show success toast
        // The user data might be stale until next page load, but the update went through
      }

      // Invalidate photos cache so edit profile page fetches fresh order on next mount
      queryClient.invalidateQueries({ queryKey: ["photos"] })

      // Invalidate intentions, traditions, and backgrounds so edit profile reflects new data
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["intentions", userId] })
      }
      queryClient.invalidateQueries({ queryKey: ["spiritual-traditions"] })
      queryClient.invalidateQueries({ queryKey: ["ethnic-backgrounds"] })

      toast.success("Profile updated successfully")
    },
    onError: (error: unknown) => {
      console.error("Profile update mutation error:", error)

      const err = error as AxiosError<ApiError>

      // Log the full error response for debugging
      if (err.response) {
        console.error("Error response:", {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
        })
      }

      // Show specific error message if available
      const message = err.response?.data?.message || "Failed to update profile"
      toast.error(message)

      // Throw error so calling component can handle it
      throw error
    },
  })
}

/**
 * Update Permissions Hook
 */
export function useUpdatePermissions() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore(state => state.setUser)
  const user = useAuthStore(state => state.user)

  return useMutation({
    mutationFn: userService.createPermissionSettings,
    onSuccess: () => {
      // Update user's onboardingCompleted flag in auth store
      if (user) {
        setUser({ ...user, onboardingCompleted: true })
      }

      // Update query cache
      queryClient.setQueryData(["user"], (old: { user: unknown } | undefined) => {
        if (!old?.user) return old
        return {
          ...old,
          user: { ...old.user, onboardingCompleted: true },
        }
      })

      toast.success("Preferences saved")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to save preferences"
      toast.error(message)
      // Throw error so calling component can handle it
      throw error
    },
  })
}
